import api from '../api/axios';

// ============ TYPES ============

export interface ActivityAttachment {
    id: number;
    activity_id: number;
    file_path: string;
    image_size: number;
    signed_url: string;
}

export interface BaseActivity {
    id: number;
    tenant_id: number;
    constituency_id: number;
    created_by: number;
    description: string | null;
    activity_type: 'development' | 'employment_creation' | 'social_welfare' | 'other';
    created_at: string;
    updated_at: string;
    constituency?: any;
    villages?: any[];
    creator?: any;
    attachments?: ActivityAttachment[];
    development_activity?: DevelopmentActivityDetail;
    employment_creation_activity?: EmploymentActivityDetail;
}

export interface DevelopmentActivityDetail {
    id: number;
    activity_id: number;
    tenant_id: number;
    department_id: number | null;
    ministry_id: number | null;
    requested_date: string | null;
    activity: string;
    status: 'pending' | 'requested' | 'in_progress' | 'completed' | 'rejected';
    rejected_reason: string | null;
    rejected_by: string | null;
    rejected_at: string | null;
    approved_by: string | null;
    approved_at: string | null;
    requested_fund: number | null;
    approved_fund: number | null;
    released_fund: number | null;
    department?: any;
    ministry?: any;
}

export interface EmploymentActivityDetail {
    id: number;
    activity_id: number;
    tenant_id: number;
    job_mela_started_at: string | null;
    job_mela_ended_at: string | null;
    organizer: string | null;
    number_of_jobs_created: number | null;
    companies?: EmploymentCompany[];
}

export interface EmploymentCompany {
    id: number;
    employment_activity_id: number;
    company_name: string;
    company_address: string | null;
    contact_person: string | null;
    contact_phone: string | null;
    number_of_jobs_created: number | null;
}

// ============ FETCH PARAMS ============

interface FetchParams {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    'constituency_id[]'?: number[];
    'village_id[]'?: number[];
}

// ============ DEVELOPMENT ACTIVITY SERVICE ============

export const developmentActivityService = {
    getAll: async (params?: FetchParams) => {
        const response = await api.get('/tenant/development-activities', { params });
        return response.data;
    },

    getById: async (id: number) => {
        const response = await api.get(`/tenant/development-activities/${id}`);
        return response.data;
    },

    create: async (data: FormData) => {
        const response = await api.post('/tenant/development-activities', data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    update: async (id: number, data: any) => {
        const response = await api.put(`/tenant/development-activities/${id}`, data);
        return response.data;
    },

    delete: async (id: number) => {
        const response = await api.delete(`/tenant/development-activities/${id}`);
        return response.data;
    },

    updateStatus: async (id: number, data: { status: string; rejected_reason?: string; approved_by?: string }) => {
        const response = await api.patch(`/tenant/development-activities/${id}/status`, data);
        return response.data;
    },

    uploadImages: async (id: number, formData: FormData) => {
        const response = await api.post(`/tenant/development-activities/${id}/images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    deleteImage: async (id: number, attachmentId: number) => {
        const response = await api.delete(`/tenant/development-activities/${id}/images/${attachmentId}`);
        return response.data;
    },

    getPdf: async (id: number) => {
        const response = await api.get(`/tenant/development-activities/${id}/pdf`, { responseType: 'blob' });
        return response.data;
    },
};

// ============ EMPLOYMENT ACTIVITY SERVICE ============

export const employmentActivityService = {
    getAll: async (params?: FetchParams) => {
        const response = await api.get('/tenant/employment-activities', { params });
        return response.data;
    },

    getById: async (id: number) => {
        const response = await api.get(`/tenant/employment-activities/${id}`);
        return response.data;
    },

    create: async (data: FormData) => {
        const response = await api.post('/tenant/employment-activities', data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    update: async (id: number, data: any) => {
        const response = await api.put(`/tenant/employment-activities/${id}`, data);
        return response.data;
    },

    delete: async (id: number) => {
        const response = await api.delete(`/tenant/employment-activities/${id}`);
        return response.data;
    },

    uploadImages: async (id: number, formData: FormData) => {
        const response = await api.post(`/tenant/employment-activities/${id}/images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    deleteImage: async (id: number, attachmentId: number) => {
        const response = await api.delete(`/tenant/employment-activities/${id}/images/${attachmentId}`);
        return response.data;
    },

    getPdf: async (id: number) => {
        const response = await api.get(`/tenant/employment-activities/${id}/pdf`, { responseType: 'blob' });
        return response.data;
    },
};
